import React, { useCallback, useEffect, useState, useRef } from "react";
import MostAskedQuestions from "./MostAskedQuestions";
import environment from "../../constants/Environment";
import { motion, AnimatePresence } from "framer-motion";
import {
  AllQuestionsIcon,
  MostAskedQuestionsIcon,
  UnansweredQueriesIcon,
  HelpfulQuestion,
  UnhelpfulQuestion,
} from "@/assets/AiBotAssets";
import AddQuestionModal from "./AddQuestionModal";
import { TabTitle } from "../../utils/TitleFuntion";
import _ from "lodash";
import NoDataOnboard from "../agent/NoDataOnboard";
import Pagination from "../cbb-builder/Pagination";
import { useResponseCache } from "@/hooks/useResponseCache";
import ModalWrapper from "../cbb-builder/components/ModalWrapper";
import { axiosPost } from "@/services/axiosService";
import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";

interface Props {
  onClose: () => void;
  page?: number;
  setPage?: any;
  selectedBot?: any;
  fromDate?: any;
  toDate?: any;
  hideFilters?: boolean;
}

function AllQuestionsModal(props: Props) {
  const {
    selectedBot,
    onClose,
    page,
    setPage,
    fromDate,
    toDate,
    hideFilters = false,
  } = props;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [openAddQuestionModal, setOpenAddQuestionModal] =
    useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [searchString, setSearchString] = useState("");

  const [inputValue, setInputValue] = useState(searchString);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isBulkUnarchiving, setIsBulkUnarchiving] = useState(false);
  const dispatch = useDispatch();

  const filters = [
    { icon: <AllQuestionsIcon />, title: "All Questions", value: "all" },
    {
      icon: <MostAskedQuestionsIcon />,
      title: "Most Asked Questions",
      value: "most_asked",
    },
    {
      icon: <UnansweredQueriesIcon />,
      title: "Unanswered Queries",
      value: "unanswered",
    },
    { icon: <HelpfulQuestion />, title: "Helpful Questions", value: "helpful" },
    {
      icon: <UnhelpfulQuestion />,
      title: "Unhelpful Questions",
      value: "unhelpful",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.0837 18.3333H3.91699C2.66699 18.3333 1.66699 17.3333 1.66699 16.0833V5.83333C1.66699 5.33333 2.00033 5 2.50033 5H17.5003C18.0003 5 18.3337 5.33333 18.3337 5.83333V16.0833C18.3337 17.3333 17.3337 18.3333 16.0837 18.3333ZM3.33366 6.66667V16.0833C3.33366 16.4167 3.58366 16.6667 3.91699 16.6667H16.0837C16.417 16.6667 16.667 16.4167 16.667 16V6.66667H3.33366Z"
            fill="currentColor"
          />
          <path
            d="M17.5 6.66602H2.50003C2.16669 6.66602 1.91669 6.49935 1.75003 6.16602C1.58336 5.83268 1.66669 5.58268 1.83336 5.33268L4.08336 2.33268C4.41669 1.91602 4.91669 1.66602 5.41669 1.66602H14.5834C15.0834 1.66602 15.5834 1.91602 15.9167 2.33268L18.1667 5.33268C18.3334 5.58268 18.4167 5.91602 18.25 6.16602C18.0834 6.49935 17.8334 6.66602 17.5 6.66602ZM4.16669 4.99935H15.8334L14.5834 3.33268H5.41669L4.16669 4.99935Z"
            fill="currentColor"
          />
        </svg>
      ),
      title: "Archived Questions",
      value: "expired",
    },
  ];

  const handleSearch = useCallback(
    _.debounce((value: string) => {
      setSearchString(value);
    }, 500),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    handleSearch(value);
  };

  const handleBulkUnarchive = async (cacheResponseIds: string[]) => {
    if (isBulkUnarchiving || cacheResponseIds.length === 0) return;

    setIsBulkUnarchiving(true);

    try {
      await axiosPost(`/analysis/unarchieve`, {
        cacheResponseId: cacheResponseIds,
      });
      setSelectedItems([]);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: `${cacheResponseIds.length} questions unarchived successfully!`,
        }),
      );
    } catch (error) {
      console.error("Error unarchiving questions:", error);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Failed to unarchive questions. Please try again.",
        }),
      );
    }

    setIsBulkUnarchiving(false);
  };

  useEffect(() => {
    TabTitle(" | All Questions");
    return () => {
      TabTitle(" | Analytics");
    };
  }, []);
  const { questions, isLoading }: any = useResponseCache({
    selectedBot,
    fromDate,
    toDate,
    selectedFilter: selectedFilter as any,
    searchString,
    page,
    pageSize: 6,
  });

  useEffect(() => {
    const element = document.querySelector(
      ".ai-bot-container",
    ) as HTMLDivElement | null;
    const mainMenuElement = document.querySelector(
      ".builder-hello-bar",
    ) as HTMLElement | null;
    const mainMenuHeader = document.querySelector(
      ".main-menu-ai-builder",
    ) as HTMLElement | null;
    const shortLeftColElement = document.querySelector(
      ".short-left-col",
    ) as HTMLElement | null;

    if (mainMenuElement) {
      mainMenuElement.setAttribute("style", "z-index: 9 !important;");
    }
    if (shortLeftColElement) {
      shortLeftColElement.setAttribute("style", "z-index: 9 !important;");
    }
    if (mainMenuHeader) {
      mainMenuHeader.setAttribute("style", "z-index: 9 !important;");
    }
    if (element) {
      element.setAttribute("style", "z-index: 0 !important;");
    }
    return () => {
      if (element) {
        element.style.zIndex = "8";
      }
      if (mainMenuElement) {
        mainMenuElement.setAttribute("style", "z-index: 9px !important;");
      }
      if (mainMenuHeader) {
        mainMenuHeader.setAttribute("style", "z-index: 9 !important;");
      }
      if (shortLeftColElement) {
        shortLeftColElement.setAttribute("style", "z-index: 9 !important;");
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef?.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        if (!searchValue) {
          setIsSearchFocused(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const itemsPerPage = 6;
  const totalItems = questions?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <>
      <div className="config-container cbb-config-container !m-[0px] !w-[100%] pb-[20px]">
        {!hideFilters && (
          <motion.div
            className={`cbb-config-menu ${isCollapsed ? "force-width" : "expand-width"}`}
            animate={{
              width: environment()?.isAI
                ? isCollapsed
                  ? "60px"
                  : "296px"
                : "296px",
            }}
            initial={{ width: 60 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <span className="cbb-config-menu-heading">
              <span className="w-100 whitespace-nowrap text-[12px]">
                Filters
              </span>
            </span>
            <motion.ul
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="config-menu-list config-qa-list"
            >
              {filters.map((filter, index) => {
                const isActive = selectedFilter === filter.value;
                return (
                  <li
                    key={filter.value}
                    className={`int-drp cursor-pointer ${isActive ? "bg-[#ffeaeb] !text-[#FB5F66]" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedFilter(filter.value);
                    }}
                  >
                    <div
                      className={`config-menu-icon ${isActive ? "activeSvg" : ""}`}
                    >
                      {filter.icon}
                    </div>
                    <div>{filter.title}</div>
                  </li>
                );
              })}
            </motion.ul>
          </motion.div>
        )}
        <div
          className={
            "general-container settings-container handoffs-outer mt-[20px] !h-[98%] min-h-[unset] rounded-[10px] bg-white shadow-[0px_0px_6px_3px_rgba(0,0,0,0.05)]"
          }
        >
          {!hideFilters && (
            <div className="mx-[20px] mt-[10px] flex items-center gap-[8px] rounded-[10px] bg-gradient-to-r from-[rgba(255,120,126,0.1)] to-[rgba(227,35,44,0.1)] px-[16px] py-[14px] text-[14px] text-[#FB5F66]">
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 4.16667H9.16667V5.83333H7.5V4.16667ZM7.5 7.5H9.16667V12.5H7.5V7.5ZM8.33333 0C3.73333 0 0 3.73333 0 8.33333C0 12.9333 3.73333 16.6667 8.33333 16.6667C12.9333 16.6667 16.6667 12.9333 16.6667 8.33333C16.6667 3.73333 12.9333 0 8.33333 0ZM8.33333 15C4.65833 15 1.66667 12.0083 1.66667 8.33333C1.66667 4.65833 4.65833 1.66667 8.33333 1.66667C12.0083 1.66667 15 4.65833 15 8.33333C15 12.0083 12.0083 15 8.33333 15Z"
                  fill="#FB5F66"
                />
              </svg>
              <span className="flex items-center">
                Updating the Source Link (Build) will permanently delete all
                existing questions and responses below. Please add to Favourite
                <svg
                  className="mx-[3px]"
                  width="15"
                  height="13"
                  viewBox="0 0 15 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.05176 4.3916L9.22656 4.74512L9.61719 4.80176L13.0889 5.30566L10.5771 7.75391L10.2939 8.0293L10.3613 8.41797L10.9541 11.876L7.84863 10.2432L7.49902 10.0605L7.15039 10.2432L4.04395 11.875L4.6377 8.41797L4.70508 8.0293L4.42188 7.75391L1.90918 5.30566L5.38184 4.80176L5.77246 4.74512L5.94629 4.3916L7.49902 1.24414L9.05176 4.3916Z"
                    fill="#FCE9EA"
                    stroke="#FB5F66"
                    strokeWidth="1.5"
                  />
                </svg>
                for Backup
              </span>
            </div>
          )}
          <div className="mb-[20px] flex justify-between px-[20px] pt-[20px]">
            <div className="flex gap-[20px]">
              {!hideFilters && (
                <motion.button
                  onClick={() => {
                    onClose();
                  }}
                  className="border-[rgba(0, 0, 0, 0.10)] group relative flex items-center justify-center rounded-[10px] border bg-white p-[12px] transition-colors hover:bg-[#f8f8f8]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className="flex items-center gap-[8px]"
                    initial={{ x: 0 }}
                    animate={{ x: 0 }}
                    whileHover={{ x: -3 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <motion.svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="transition-transform"
                      animate={{
                        rotate: 0,
                        x: 0,
                      }}
                      variants={{
                        hover: {
                          rotate: [-10, 0],
                          x: -2,
                          transition: {
                            duration: 0.5,
                            ease: "easeInOut",
                            times: [0, 1],
                          },
                        },
                      }}
                    >
                      <motion.path
                        d="M16 6.9899V9.0101H3.87879L9.43434 14.5657L8 16L0 8L8 0L9.43434 1.43434L3.87879 6.9899H16Z"
                        fill="#6D6D6D"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                      />
                    </motion.svg>
                  </motion.div>
                </motion.button>
              )}
              <div className="grid">
                <div className="flex gap-[7px]">
                  <h2 className="text-[16px] font-medium text-text-clr">
                    Improve your Agent's Responses
                  </h2>
                  <svg
                    className="cursor-pointer"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.1665 5.83268H10.8332V7.49935H9.1665V5.83268ZM9.1665 9.16602H10.8332V14.166H9.1665V9.16602ZM9.99984 1.66602C5.39984 1.66602 1.6665 5.39935 1.6665 9.99935C1.6665 14.5993 5.39984 18.3327 9.99984 18.3327C14.5998 18.3327 18.3332 14.5993 18.3332 9.99935C18.3332 5.39935 14.5998 1.66602 9.99984 1.66602ZM9.99984 16.666C6.32484 16.666 3.33317 13.6743 3.33317 9.99935C3.33317 6.32435 6.32484 3.33268 9.99984 3.33268C13.6748 3.33268 16.6665 6.32435 16.6665 9.99935C16.6665 13.6743 13.6748 16.666 9.99984 16.666Z"
                      fill="#666666"
                    />
                  </svg>
                </div>
                <p className="text-[14px] text-text-clr">
                  Understand questions your users are asking and how is agent
                  responding to them.
                </p>
              </div>
            </div>
            <div className="flex gap-[20px]">
              {/* Debug: Show current selectedFilter */}
              <div className="text-xs text-gray-500">
                Current filter: {selectedFilter}
              </div>
              {(selectedFilter === "expired" || true) && (
                <motion.button
                  onClick={() => {
                    if (selectedItems && selectedItems.length > 0) {
                      handleBulkUnarchive(selectedItems);
                    }
                  }}
                  disabled={
                    !selectedItems ||
                    selectedItems.length === 0 ||
                    isBulkUnarchiving
                  }
                  className="group flex items-center gap-[5px] rounded-[10px] border border-orange-500 bg-orange-50 px-[16px] py-[12px] font-medium text-orange-600 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <div className="flex items-center transition-transform duration-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M10.1664 8.33468C10.333 8.33468 10.583 8.41801 10.6664 8.50134L13.1664 10.5847C13.4997 10.918 13.583 11.418 13.2497 11.7513C12.9164 12.0847 12.4164 12.168 12.083 11.8347L10.083 10.168L8.08305 11.8347C7.74971 12.168 7.16638 12.0847 6.91638 11.7513C6.58305 11.418 6.66638 10.8347 6.99971 10.5847L9.49971 8.50134C9.74971 8.41801 9.99971 8.33468 10.1664 8.33468Z"
                        fill="currentColor"
                      />
                      <path
                        d="M10.1657 8.33333C10.6657 8.33333 10.999 8.66667 10.999 9.16667V14.1667C10.999 14.6667 10.6657 15 10.1657 15C9.66569 15 9.33236 14.6667 9.33236 14.1667V9.16667C9.33236 8.75 9.66569 8.33333 10.1657 8.33333Z"
                        fill="currentColor"
                      />
                      <path
                        d="M16.0837 18.3333H3.91699C2.66699 18.3333 1.66699 17.3333 1.66699 16.0833V5.83333C1.66699 5.33333 2.00033 5 2.50033 5H17.5003C18.0003 5 18.3337 5.33333 18.3337 5.83333V16.0833C18.3337 17.3333 17.3337 18.3333 16.0837 18.3333ZM3.33366 6.66667V16.0833C3.33366 16.4167 3.58366 16.6667 3.91699 16.6667H16.0837C16.417 16.6667 16.667 16.4167 16.667 16V6.66667H3.33366Z"
                        fill="currentColor"
                      />
                      <path
                        d="M17.5 6.66602H2.50003C2.16669 6.66602 1.91669 6.49935 1.75003 6.16602C1.58336 5.83268 1.66669 5.58268 1.83336 5.33268L4.08336 2.33268C4.41669 1.91602 4.91669 1.66602 5.41669 1.66602H14.5834C15.0834 1.66602 15.5834 1.91602 15.9167 2.33268L18.1667 5.33268C18.3334 5.58268 18.4167 5.91602 18.25 6.16602C18.0834 6.49935 17.8334 6.66602 17.5 6.66602ZM4.16669 4.99935H15.8334L14.5834 3.33268H5.41669L4.16669 4.99935Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <motion.span
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {isBulkUnarchiving
                      ? "Unarchiving..."
                      : `Unarchive All (${selectedItems?.length || 0})`}
                  </motion.span>
                </motion.button>
              )}
              <motion.div
                ref={searchContainerRef}
                className="border-[rgba(0, 0, 0, 0.10)] flex cursor-pointer items-center gap-[10px] overflow-hidden rounded-[10px] border bg-white px-[16px] py-[12px]"
                initial={{ width: "50px" }}
                animate={{
                  width: isSearchFocused || !!searchString ? "227px" : "50px",
                  transition: { duration: 0.3, ease: "easeInOut" },
                }}
                onClick={() => {
                  setIsSearchFocused(true);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, rotate: 0 }}
                  animate={{
                    opacity: 1,
                    rotate: isSearchFocused ? 90 : 0,
                    scale: isSearchFocused ? 1.1 : 1,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    rotate: {
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                    },
                  }}
                  className="flex-shrink-0 origin-center"
                >
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_5574_63738)">
                      <path
                        d="M5.2 0.5C6.57913 0.5 7.90177 1.04786 8.87696 2.02304C9.85215 2.99823 10.4 4.32087 10.4 5.7C10.4 6.988 9.928 8.172 9.152 9.084L9.368 9.3H10L14 13.3L12.8 14.5L8.8 10.5V9.868L8.584 9.652C7.64044 10.4574 6.44057 10.8999 5.2 10.9C3.82087 10.9 2.49823 10.3521 1.52304 9.37696C0.547855 8.40177 0 7.07913 0 5.7C0 4.32087 0.547855 2.99823 1.52304 2.02304C2.49823 1.04786 3.82087 0.5 5.2 0.5ZM5.2 2.1C3.2 2.1 1.6 3.7 1.6 5.7C1.6 7.7 3.2 9.3 5.2 9.3C7.2 9.3 8.8 7.7 8.8 5.7C8.8 3.7 7.2 2.1 5.2 2.1Z"
                        fill="black"
                        fillOpacity="0.5"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5574_63738">
                        <rect
                          width="14"
                          height="14"
                          fill="white"
                          transform="translate(0 0.5)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </motion.div>

                <AnimatePresence>
                  {(isSearchFocused || searchString) && (
                    <motion.input
                      initial={{
                        opacity: 0,
                        width: !!searchString ? "100%" : 0,
                      }}
                      autoFocus
                      animate={{ opacity: 1, width: "100%" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="h-auto flex-grow border-0 border-none bg-transparent p-0 outline-none [&::-webkit-search-cancel-button]:hidden"
                      type="search"
                      placeholder="Search a question"
                      value={inputValue}
                      onChange={(e) => handleInputChange(e)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={(e) => {
                        if (
                          !e.relatedTarget ||
                          !e.currentTarget.parentElement?.contains(
                            e.relatedTarget as Node,
                          )
                        ) {
                          if (!searchValue) {
                            setIsSearchFocused(false);
                          }
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSearchFocused(true);
                      }}
                    />
                  )}
                </AnimatePresence>
              </motion.div>
              <motion.button
                onClick={() => setOpenAddQuestionModal(true)}
                className="group flex items-center gap-[5px] rounded-[10px] border border-primary-clr bg-white px-[16px] py-[12px] font-medium text-primary-clr transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <div className="flex items-center transition-transform duration-300 [transform:rotate(var(--rotation,0deg))] group-hover:[--rotation:180deg]">
                  <motion.svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_5574_63741)">
                      <motion.path
                        d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433286 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9971 7.34872 18.9426 4.80684 17.0679 2.9321C15.1932 1.05736 12.6513 0.00286757 10 0ZM10 18.3333C8.35183 18.3333 6.74066 17.8446 5.37025 16.9289C3.99984 16.0132 2.93174 14.7117 2.30101 13.189C1.67028 11.6663 1.50525 9.99076 1.82679 8.37425C2.14834 6.75774 2.94201 5.27288 4.10745 4.10744C5.27289 2.94201 6.75774 2.14833 8.37425 1.82679C9.99076 1.50525 11.6663 1.67027 13.189 2.301C14.7118 2.93173 16.0132 3.99984 16.9289 5.37025C17.8446 6.74066 18.3333 8.35182 18.3333 10C18.3309 12.2094 17.4522 14.3276 15.8899 15.8899C14.3276 17.4522 12.2094 18.3309 10 18.3333ZM14.1667 10C14.1667 10.221 14.0789 10.433 13.9226 10.5893C13.7663 10.7455 13.5544 10.8333 13.3333 10.8333H10.8333V13.3333C10.8333 13.5543 10.7455 13.7663 10.5893 13.9226C10.433 14.0789 10.221 14.1667 10 14.1667C9.77899 14.1667 9.56703 14.0789 9.41075 13.9226C9.25447 13.7663 9.16667 13.5543 9.16667 13.3333V10.8333H6.66667C6.44566 10.8333 6.2337 10.7455 6.07742 10.5893C5.92113 10.433 5.83334 10.221 5.83334 10C5.83334 9.77899 5.92113 9.56703 6.07742 9.41074C6.2337 9.25447 6.44566 9.16667 6.66667 9.16667H9.16667V6.66667C9.16667 6.44565 9.25447 6.23369 9.41075 6.07741C9.56703 5.92113 9.77899 5.83333 10 5.83333C10.221 5.83333 10.433 5.92113 10.5893 6.07741C10.7455 6.23369 10.8333 6.44565 10.8333 6.66667V9.16667H13.3333C13.5544 9.16667 13.7663 9.25447 13.9226 9.41074C14.0789 9.56703 14.1667 9.77899 14.1667 10Z"
                        fill="currentColor"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5574_63741">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </motion.svg>
                </div>
                <motion.span
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  Add Question
                </motion.span>
              </motion.button>
            </div>
          </div>
          <hr className="mx-auto w-[97%]" />
          <div className="mt-[20px] h-[calc(100%-155px)] overflow-y-auto rounded-[10px] px-[20px]">
            <MostAskedQuestions
              pageSize={6}
              page={page}
              selectedFilter={selectedFilter}
              selectedBot={selectedBot}
              fromDate={fromDate}
              toDate={toDate}
              searchString={searchString}
              setPage={setPage}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          </div>
        </div>
      </div>
      {openAddQuestionModal && (
        <ModalWrapper>
          <AddQuestionModal
            id={selectedBot?.botCustomId}
            onClose={() => setOpenAddQuestionModal(false)}
            type="add"
            preserved={hideFilters}
          />
        </ModalWrapper>
      )}
    </>
  );
}

export default AllQuestionsModal;
